// @ts-nocheck

import { GeocodeRequester, type LatLng } from '@cobliteam/api-lib';
import { GeofenceTypeItems, colors } from '@cobliteam/base-components';
import { Haversine } from '@cobliteam/common-types';
import { type GeofenceGeometryPoint, type GeofenceGeometryPolygon, GeofenceType } from '@cobliteam/common-types/src/geofenceV3';

import { Environment } from '@cobliteam/react-environments';
import { type AppState, changeDrawLayers } from '@cobliteam/store';
import {
  type FitBoundsOptions,
  type LatLngBoundsExpression,
  type LatLngExpression,
  type Map as LeafletMap,
  Layer as MapLayer,
  type PM,
  type PointExpression,
  type ZoomPanOptions,
} from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';

import styles from './styles';

const FIT_BOUND_PADDING: PointExpression = [100, 100];

const env = Environment.getInstance().getEnvironment();
const geocodeRequester = new GeocodeRequester({ host: env.herbieApiHost });

const customTranslation = {
  tooltips: {
    // Círculo
    startCircle: 'Clique e arraste no mapa para definir o tamanho do local',
    finishCircle: 'Clique para finalizar o círculo',

    // Retângulo
    firstVertex: 'Clique e arraste no mapa para desenhar o local',
    finishRect: 'Clique para finalizar',

    // Polígono
    continueLine: 'Clique para continuar o desenho',
    finishPoly: 'Clique no primeiro ponto para finalizar o desenho',
  },
};

interface FlyToProps {
  latlng: LatLngExpression;
  zoom?: number;
  options?: ZoomPanOptions;
}

const useMap = () => {
  const map: LeafletMap = useSelector((state: AppState) => state.map.mapRef);
  const drawLayers = useSelector((state: AppState) => state.map.drawLayers);
  const dispatch = useDispatch();

  const mapZoom = map?.getZoom();

  const { pointsOnCircle } = Haversine;

  const flyTo = ({ latlng, zoom = 16, options = { duration: 0.5 } }: FlyToProps) => {
    map.flyTo(latlng, zoom, options);
  };

  const openPopup = (ref: MapLayer | any, delay = 500) =>
    setTimeout(() => {
      if (ref instanceof MapLayer) {
        ref.openPopup();
        return;
      }

      if (ref && !ref.openPopup()) {
        ref.openPopup();
      }
    }, delay);

  const setView = ({ latlng, zoom = 16 }: FlyToProps) => {
    map?.setView(latlng, zoom);
  };

  const setToBounds = (bounds: LatLngBoundsExpression, options?: FitBoundsOptions) => {
    if (Object.keys(bounds).length) {
      map.fitBounds(bounds, {
        duration: 0.5,
        padding: FIT_BOUND_PADDING,
        ...options,
      });
    }
  };

  const flyToBounds = (bounds: LatLngBoundsExpression, options?: FitBoundsOptions) => {
    if (Object.keys(bounds).length) {
      map.flyToBounds(bounds, {
        duration: 0.5,
        padding: FIT_BOUND_PADDING,
        ...options,
      });
    }
  };

  const getAddressByLatLng = async (latlng: LatLng) => {
    const address = await geocodeRequester.getAddress(latlng);
    return address;
  };

  const parseToGeometryPoint = (latlng: LatLng): GeofenceGeometryPoint => {
    return {
      type: 'Point',
      coordinates: [latlng.lng, latlng.lat],
    };
  };

  const parseToGeometryPolygon = (coordinates: number[][]): GeofenceGeometryPolygon => {
    return {
      type: 'Polygon',
      coordinates: [coordinates],
    };
  };

  const getDrawLayers = () => {
    return drawLayers;
  };

  const setDrawLayers = (layer: L.Layer) => {
    dispatch(changeDrawLayers([layer]));
  };

  const clearDrawLayers = () => {
    const layer = getDrawLayers()[0];
    if (layer) layer.remove();

    dispatch(changeDrawLayers([]));
  };

  const bindTooltipInDrawLayers = (title: string) => {
    const layer = getDrawLayers()[0];

    layer
      ?.bindTooltip(title, {
        permanent: true,
        direction: 'bottom',
        sticky: true,
        offset: [0, 10],
        className: styles.tooltip,
      })
      .addEventListener({
        mouseover: () => {
          layer.openTooltip();
        },
        mouseout: () => {
          layer.closeTooltip();
        },
      });
  };

  const unbindTooltipInDrawLayers = () => {
    const layer = getDrawLayers()[0];

    layer?.unbindTooltip();
  };

  const setGeofenceStyle = (type: GeofenceType = GeofenceType.OTHER, id = '') => {
    const color = GeofenceTypeItems[type].activeColor;

    const drawOptions: PM.DrawModeOptions = {
      templineStyle: { color },
      hintlineStyle: {
        color: colors.gray.dark,
        stroke: true,
        dashArray: [5, 5],
      },
      pathOptions: { color },
    };
    map.pm.setGlobalOptions(drawOptions);

    const layerGroup = map.pm.getGeomanDrawLayers(true);
    const layer = getDrawLayers()[0];

    if (layer) {
      if (layerGroup.getLayers().length) layerGroup.setStyle({ color, className: styles.layer(color) });
      if (layer.getPane) {
        const layerPath = layer?.getPane('overlayPane')?.getElementsByClassName(id);

        // This is a workaround to remove the last class added in the layer
        const element = layerPath?.[0];

        if (element) {
          // remove all class
          element.removeAttribute('class');

          // add class to element
          element.classList.add('leaflet-interactive');
          element.classList.add(id);
          element.classList.add(styles.layer(color));
        }
      }
    }
  };

  const setTooltipStyle = () => {
    map.pm.setLang('pt_br', customTranslation, 'en');

    const layer = getDrawLayers()[0];
    const tooltip = layer?.getPane('tooltipPane');

    tooltip?.setAttribute('class', `${tooltip.className} ${styles.tooltip}`);
  };

  return {
    map,
    mapZoom,
    flyTo,
    flyToBounds,
    setView,
    setToBounds,
    openPopup,
    pointsOnCircle,
    parseToGeometryPoint,
    parseToGeometryPolygon,
    getAddressByLatLng,
    bindTooltipInDrawLayers,
    unbindTooltipInDrawLayers,
    setGeofenceStyle,
    setTooltipStyle,
    getDrawLayers,
    setDrawLayers,
    clearDrawLayers,
  };
};

export default useMap;
